import { supabase } from "./supabaseClient";
import { getBrowserSupabaseKey } from "./browserSupabaseKey";

function trimText(value) {
  return String(value ?? '').trim();
}

async function getStaffAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const accessToken = session?.access_token;
  if (!accessToken) {
    return null;
  }

  return accessToken;
}

function getFunctionUrl(name) {
  const supabaseUrl = trimText(process.env.REACT_APP_SUPABASE_URL);
  if (!supabaseUrl) {
    throw new Error('Missing Supabase function URL configuration.');
  }

  return `${supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`;
}

async function readEdgeFunctionError(response) {
  const contentType = trimText(response.headers.get('content-type')).toLowerCase();

  if (contentType.includes('application/json')) {
    const data = await response.json().catch(() => null);
    return trimText(data?.error) || trimText(data?.message) || `Request failed with status ${response.status}.`;
  }

  const text = await response.text().catch(() => '');
  return trimText(text) || `Request failed with status ${response.status}.`;
}

async function invokeStaffFunctionWithBody(name, body) {
  const accessToken = await getStaffAccessToken();
  if (!accessToken) {
    return {
      data: {
        ok: false,
        code: "UNAUTHENTICATED",
        error: "A valid staff sign-in is required.",
      },
      error: null,
    };
  }

  return supabase.functions.invoke(name, {
    body,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

export async function invokeStaffFunction(name, body) {
  return invokeStaffFunctionWithBody(name, body);
}

export async function invokeStaffMultipartFunction(name, formData) {
  return invokeStaffFunctionWithBody(name, formData);
}

export async function invokeStaffBlobFunction(name, body = {}) {
  const accessToken = await getStaffAccessToken();
  if (!accessToken) {
    throw new Error('A valid staff sign-in is required.');
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };
  // The caller is authorized by the staff access token above. This is only the
  // public project key the gateway expects alongside it.
  const publicKey = getBrowserSupabaseKey();
  if (publicKey) {
    headers.apikey = publicKey;
  }

  const response = await fetch(getFunctionUrl(name), {
    method: 'POST',
    headers,
    body: JSON.stringify(body ?? {}),
  });

  if (!response.ok) {
    throw new Error(await readEdgeFunctionError(response));
  }

  return response.blob();
}
